import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';

// DTOs based on the API endpoints
export interface AvailableMixesResponseDto {
  availableMixIds: string[];
}

export interface CooperativeTestDto {
  isSelected: boolean;
  type: string;
}

export interface CooperativeMixDto {
  slurryId: number;
  name: string;
  ufSlurryID: string;
  type: number;
  tests: CooperativeTestDto[];
}

export interface CooperativeTestReportDataDto {
  projectName: string;
  pipeSize: string;
  jobType: string;
  jobSubtype: string;
  requestedDate: string;
  dueDate: string;
  wellName: string;
  wellLocation: string;
  businessUnit: string;
  wbs: string;
  serviceCompany: string;
  rigName: string;
  notes: string;
  measuredDepth: number;
  totalVerticalDepth: number;
  bhst: number;
  bhct: number;
  mixes: PrintableMixDto[];
}

export interface PrintableMixDto {
  name: string;
  dryMaterials: PrintableMaterialDto[];
  liquidMaterials: PrintableMaterialDto[];
  tests: string[];
  density: number;
  yield: number;
  thickeningTimeTargetFromMins: number | null;
  thickeningTimeTargetToMins: number | null;
}

export interface PrintableMaterialDto {
  name: string;
  preferredContainer: string | null;
  containerLocation: string | null;
  labWeight: number;
  weight: number;
  volume: number;
  percent: number;
  isPercentCalculated: boolean;
  pozSackWeight: number;
  isFluidLossTestWithFoamNeeded: boolean;
}

export interface CooperativeTestRequestDto {
  projectId: number;
  requestedDate: string;
  dueDate: string;
  notes: string;
  mixIds: number[];
}

export interface CooperativeTestResponseDto {
  requestId: number;
  projectId: number;
  status: string;
  createdDate: string;
  message: string;
}

@Injectable({ providedIn: 'root' })
export class CooperativeTestService {
  private readonly baseUrl = environment.apiUrl;

  constructor(private http: HttpClient) { }

  /**
   * Get available mixes for adding to cooperative test request
   * Mirrors WPF 'Add Mix To Report' functionality
   */
  getAvailableMixes(projectId: number): Observable<any> {
    return this.http.get(
      `${this.baseUrl}/project-details/available-mixes/${projectId}`
    );
  }

  /**
   * Get cooperative mix with all tests by UFSlurryID
   */
  getMixByUfSlurryId(projectId: number, ufSlurryId: string): Observable<CooperativeMixDto> {
    return this.http.get<CooperativeMixDto>(
      `${this.baseUrl}/project-details/mix/${projectId}/${encodeURIComponent(ufSlurryId)}`
    );
  }

  /**
   * Create a new cooperative test request
   */
  createCooperativeTestRequest(request: CooperativeTestRequestDto): Observable<CooperativeTestResponseDto> {
    return this.http.post<CooperativeTestResponseDto>(
      `${this.baseUrl}/project-details/cooperative-test-request`,
      request
    );
  }

  /**
   * Get cooperative test report data for PDF generation
   */
  getCooperativeTestReportData(projectId: number, slurryData: any): Observable<CooperativeTestReportDataDto> {
    return this.http.post<CooperativeTestReportDataDto>(
      `${this.baseUrl}/project-details/request-cooperative-test/${projectId}`,
      slurryData
    );
  }

  /**
   * Get project details by ID (using existing endpoint)
   */
  getProjectById(id: number): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/project-details/${id}`);
  }
}
